import { Routes, Route, Navigate } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import enUS from "date-fns/locale/en-US";
import { ModalProvider } from "./components/Modal";
import {
  NavigationBlockerProvider,
  NavigationOriginProvider,
} from "./contexts";
import ProtectedRoute from "./components/ProtectedRoute";
import PublicRoute from "./components/PublicRoute";
import { useAuthInit } from "./hooks/useAuthInit";
import { useTokenManager } from "./hooks/useTokenManager";
import { useInactivityTimer } from "./hooks";
import { useAppSelector } from "./hooks/redux";
import { ROUTES } from "./constants";
import { UserRole } from "./services/usersService";

// Auth pages
import LoginPage from "./pages/Auth/LoginPage/LoginPage";
import RegisterPage from "./pages/Auth/RegisterPage/RegisterPage";
import ForgotPasswordPage from "./pages/Auth/ForgotPasswordPage";
import CheckEmailPage from "./pages/Auth/CheckEmailPage";
import CreatePasswordPage from "./pages/Auth/CreatePasswordPage";
import VerificationPage from "./pages/Auth/VerificationPage";
import BackupCodePage from "./pages/Auth/BackupCodePage";
import TwoFactorAuthPage from "./pages/Auth/TwoFactorAuthPage";
import SaveBackupCodesPage from "./pages/Auth/SaveBackupCodesPage";
import SaveBackupCodesSuccessPage from "./pages/Auth/SaveBackupCodesPage/SuccessPage";
import RegistrationErrorPage from "./pages/Auth/RegistrationErrorPage";
import RegistrationSuccessPage from "./pages/Auth/RegistrationSuccessPage";

// Main pages
import Dashboard from "./pages/Dashboard";
import AllSessionsPage from "./pages/AllSessionsPage";
import SessionDetailsPage from "./pages/SessionDetailsPage";
import TreatmentPlanPage from "./pages/TreatmentPlanPage";
import EditTreatmentPlanPage from "./pages/EditTreatmentPlanPage";
import { TreatmentPlanReviewPage } from "./pages/TreatmentPlanReviewPage";
import { TreatmentPlanApprovedPage } from "./pages/TreatmentPlanApprovedPage";
import { TreatmentPlanRejectedPage } from "./pages/TreatmentPlanRejectedPage";
import AllPatientsPage from "./pages/AllPatientsPage";
import { PatientProfilePage } from "./pages/PatientProfilePage";
import UserManagementPage from "./pages/UserManagementPage";
import { UserProfilePage, EditUserProfilePage } from "./pages/UserProfilePage";
import ManageOrganizationPage from "./pages/ManageOrganizationPage";
import AuditLogsPage from "./pages/AuditLogsPage";
import SuccessPage from "./pages/Auth/ForgotPasswordPage/SuccessPage";

// OTP testing page (public)
import OTPPage from "./pages/OTPPage";

// Legal pages
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";

function AppContent() {
  // Manage token lifecycle (proactive refresh + cross-tab sync)
  useTokenManager();

  // Get authentication status from Redux
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  useInactivityTimer(isAuthenticated ? 60 : 0);

  return (
    <Routes>
      {/* Auth routes - redirect to dashboard if already authenticated */}
      <Route
        path={ROUTES.LOGIN}
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route path={ROUTES.REGISTRATION_WITH_TOKEN} element={<RegisterPage />} />
      <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
      <Route path={ROUTES.CHECK_EMAIL} element={<CheckEmailPage />} />
      <Route
        path={ROUTES.CREATE_PASSWORD_WITH_TOKEN}
        element={<CreatePasswordPage />}
      />
      <Route path={ROUTES.SUCCESS} element={<SuccessPage />} />
      <Route path={ROUTES.VERIFICATION} element={<VerificationPage />} />
      <Route path={ROUTES.BACKUP_CODE} element={<BackupCodePage />} />
      <Route path={ROUTES.TWO_FACTOR_AUTH} element={<TwoFactorAuthPage />} />
      <Route
        path={ROUTES.SAVE_BACKUP_CODES}
        element={<SaveBackupCodesPage />}
      />
      <Route
        path={ROUTES.SAVE_BACKUP_CODES_SUCCESS}
        element={<SaveBackupCodesSuccessPage />}
      />
      <Route
        path={ROUTES.REGISTRATION_SUCCESS}
        element={<RegistrationSuccessPage />}
      />
      <Route
        path={ROUTES.REGISTRATION_ERROR}
        element={<RegistrationErrorPage />}
      />

      {/* TODO: Temporary page for testing OTP retrieval, remove in production */}
      <Route path={ROUTES.OTP} element={<OTPPage />} />

      {/* Legal pages - public */}
      <Route path={ROUTES.TERMS} element={<TermsPage />} />
      <Route path={ROUTES.PRIVACY} element={<PrivacyPage />} />

      {/* Treatment plan review - public (for patient approval via QR code) */}
      <Route
        path={ROUTES.TREATMENT_PLAN_REVIEW_APPROVED}
        element={<TreatmentPlanApprovedPage />}
      />
      <Route
        path={ROUTES.TREATMENT_PLAN_REVIEW_REJECTED}
        element={<TreatmentPlanRejectedPage />}
      />
      <Route
        path={ROUTES.TREATMENT_PLAN_REVIEW}
        element={<TreatmentPlanReviewPage />}
      />

      {/* Main app routes - protected */}
      <Route
        path={ROUTES.DASHBOARD}
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.SESSIONS}
        element={
          <ProtectedRoute>
            <AllSessionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.SESSION_DETAILS}
        element={
          <ProtectedRoute>
            <SessionDetailsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.TREATMENT_PLAN}
        element={
          <ProtectedRoute>
            <TreatmentPlanPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.TREATMENT_PLAN_EDIT}
        element={
          <ProtectedRoute>
            <EditTreatmentPlanPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.PATIENTS}
        element={
          <ProtectedRoute>
            <AllPatientsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.PATIENT_PROFILE}
        element={
          <ProtectedRoute>
            <PatientProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.PROFILE}
        element={
          <ProtectedRoute>
            <UserProfilePage isCurrentUser />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.PROFILE_EDIT}
        element={
          <ProtectedRoute>
            <EditUserProfilePage isCurrentUser />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.USER_MANAGEMENT}
        element={
          <ProtectedRoute allowedRoles={[UserRole.Manager]}>
            <UserManagementPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.USER_PROFILE}
        element={
          <ProtectedRoute>
            <UserProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.USER_PROFILE_EDIT}
        element={
          <ProtectedRoute>
            <EditUserProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.MANAGE_ORGANIZATION}
        element={
          <ProtectedRoute allowedRoles={[UserRole.Manager]}>
            <ManageOrganizationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.AUDIT_LOGS}
        element={
          <ProtectedRoute allowedRoles={[UserRole.Manager]}>
            <AuditLogsPage />
          </ProtectedRoute>
        }
      />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
    </Routes>
  );
}

function App() {
  // Initialize auth on app start
  useAuthInit();

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enUS}>
      <NavigationOriginProvider>
        <NavigationBlockerProvider>
          <ModalProvider>
            <AppContent />
          </ModalProvider>
        </NavigationBlockerProvider>
      </NavigationOriginProvider>
    </LocalizationProvider>
  );
}

export default App;
